import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingCart, Star, Tag, Eye } from 'lucide-react';
import { useCartStore } from '../../store/cartStore';
import { formatPrice, getImagePlaceholder } from '../../lib/utils';
import type { Product } from '../../types';
import toast from 'react-hot-toast';
import { useMemo } from 'react';

interface ProductCardProps {
    product: Product;
    index?: number;
}

export function ProductCard({ product, index = 0 }: ProductCardProps) {
    const addItem = useCartStore((state) => state.addItem);
    const openCart = useCartStore((state) => state.openCart);

    const price = product.sellingPrice || product.price;
    const hasDiscount = product.sellingPrice && product.sellingPrice < product.price;
    const discountPercent = hasDiscount
        ? Math.round(((product.price - product.sellingPrice!) / product.price) * 100)
        : 0;

    const mainImage = product.imageUrl || product.images?.[0]?.url || getImagePlaceholder();
    const secondaryImage = product.images?.[1]?.url;

    // Badge Logic
    const isNew = new Date(product.createdAt).getTime() > Date.now() - 7 * 24 * 60 * 60 * 1000;
    const isLowStock = product.stockStatus === 'lowstock';

    // Generate consistent random stats based on product ID (matches ProductPage)
    const stats = useMemo(() => {
        let hash = 0;
        for (let i = 0; i < product.id.length; i++) {
            hash = ((hash << 5) - hash) + product.id.charCodeAt(i);
            hash = hash & hash;
        }
        return {
            rating: 5, // Full 5 stars like ProductPage
            reviews: Math.abs(hash % 991) + 7, // Range: 7-997 (matches ProductPage)
            sold: 50 + (Math.abs(hash) % 200),
        };
    }, [product.id]);

    const handleAddToCart = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        addItem({
            productId: product.id,
            name: product.name,
            price: price,
            imageUrl: mainImage,
        });

        toast.success('Ajouté au panier!', {
            icon: '🛒',
            duration: 2000,
            style: {
                borderRadius: '12px',
                background: '#10b981',
                color: '#fff',
                fontWeight: 600,
            }
        });

        openCart();
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: Math.min(index * 0.03, 0.3) }}
            className="h-full"
        >
            <Link
                to={`/product/${product.categorySlug || 'uncategorized'}/${product.slug || product.id}`}
                className="group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 h-full flex flex-col border border-gray-100"
            >
                {/* Image Container */}
                <div className="relative aspect-square bg-gray-50 overflow-hidden">
                    {/* Main Image */}
                    <img
                        src={mainImage}
                        alt={product.name}
                        className={`absolute inset-0 w-full h-full object-cover transition-all duration-500 ${secondaryImage ? 'group-hover:opacity-0' : 'group-hover:scale-105'
                            }`}
                        loading="lazy"
                    />

                    {/* Secondary Image (Hover) */}
                    {secondaryImage && (
                        <img
                            src={secondaryImage}
                            alt={product.name}
                            className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                            loading="lazy"
                        />
                    )}

                    {/* Badges - Top Left */}
                    <div className="absolute top-2 left-2 flex flex-col gap-1.5 z-10">
                        {hasDiscount && (
                            <span className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-2.5 py-1 text-[10px] md:text-xs font-bold rounded-lg shadow-lg">
                                -{discountPercent}%
                            </span>
                        )}
                        {isNew && !hasDiscount && (
                            <span className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-2.5 py-1 text-[10px] md:text-xs font-bold rounded-lg shadow-lg">
                                NOUVEAU
                            </span>
                        )}
                        {isLowStock && (
                            <span className="bg-amber-500 text-white px-2.5 py-1 text-[10px] md:text-xs font-bold rounded-lg shadow-lg animate-pulse">
                                🔥 Stock limité
                            </span>
                        )}
                    </div>

                    {/* Sold Badge - Top Right */}
                    <div className="absolute top-2 right-2 z-10">
                        <span className="bg-black/60 backdrop-blur-sm text-white px-2 py-1 text-[10px] font-medium rounded-lg flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {stats.sold}+ vendus
                        </span>
                    </div>
                </div>

                {/* Product Info */}
                <div className="p-3 flex flex-col flex-grow">
                    {/* Rating */}
                    <div className="flex items-center gap-1 mb-1">
                        <div className="flex items-center">
                            {[...Array(5)].map((_, i) => (
                                <Star
                                    key={i}
                                    className={`w-3 h-3 ${i < Math.floor(stats.rating)
                                            ? 'text-amber-400 fill-amber-400'
                                            : 'text-gray-200'
                                        }`}
                                />
                            ))}
                        </div>
                        <span className="text-[10px] text-gray-500">({stats.reviews})</span>
                    </div>

                    {/* Title */}
                    <h3 className="font-semibold text-gray-900 text-xs md:text-sm leading-snug mb-2 line-clamp-2 min-h-[2.25rem] group-hover:text-primary-600 transition-colors">
                        {product.name}
                    </h3>

                    {/* Price & COD */}
                    <div className="mb-3">
                        <div className="flex items-baseline gap-2 flex-wrap">
                            <span className="font-bold text-base md:text-lg text-gray-900">
                                {formatPrice(price)}
                            </span>
                            {hasDiscount && (
                                <span className="text-gray-400 line-through text-xs">
                                    {formatPrice(product.price)}
                                </span>
                            )}
                        </div>
                        {product.category && (
                            <div className="flex items-center gap-1 mt-1 text-[10px] text-gray-500 font-medium">
                                <Tag className="w-3 h-3" />
                                <span className="truncate">{product.category}</span>
                            </div>
                        )}
                    </div>

                    {/* Add to Cart Button - Always Visible */}
                    <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={handleAddToCart}
                        className="mt-auto w-full bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white py-2.5 rounded-xl font-semibold text-xs md:text-sm flex items-center justify-center gap-2 shadow-md shadow-primary-500/20 transition-all"
                    >
                        <ShoppingCart className="w-4 h-4" />
                        Ajouter au panier
                    </motion.button>
                </div>
            </Link>
        </motion.div>
    );
}

// Skeleton loader for ProductCard
export function ProductCardSkeleton() {
    return (
        <div className="card overflow-hidden animate-pulse bg-white border border-gray-100">
            {/* Image Skeleton */}
            <div className="aspect-[4/5] bg-gray-100" />

            {/* Content Skeleton */}
            <div className="p-4 space-y-3">
                <div className="h-3 w-16 bg-gray-100 rounded" />
                <div className="space-y-2">
                    <div className="h-4 w-full bg-gray-100 rounded" />
                    <div className="h-4 w-2/3 bg-gray-100 rounded" />
                </div>
                <div className="h-5 w-24 bg-gray-100 rounded mt-2" />
            </div>
        </div>
    );
}
